import { handleHttpResponse } from "../../utils/http_response_utils";

// Mirrors MAX_PREFLIGHT_COMPANIES in server/modules/classification_pipeline/preflight_checks.py
export const PREFLIGHT_COMPANY_LIMIT = 10;

/**
 * Run the classification pipeline's pre-run data checks without starting a pipeline.
 *
 * Synchronous on the server — the results come back in the response, so there's no
 * task id to poll. Capped server-side at 10 companies per request.
 *
 * @param {string[]} companies - Company names to check
 * @param {string[]} platforms - Platform categories; these decide which checks run
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 */
export const runPreflightChecks = async (companies, platforms, startDate, endDate) => {
  const accessToken = localStorage.getItem("access-token");
  const response = await fetch("/api/run-preflight-checks", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      companies,
      platforms,
      start_date: startDate,
      end_date: endDate,
    }),
  });
  return await handleHttpResponse(response);
};

/**
 * Check any number of companies, splitting them across requests.
 *
 * A pipeline batch has no company limit but the endpoint does, so this chunks to
 * PREFLIGHT_COMPANY_LIMIT and runs the chunks one at a time rather than opening every
 * DB connection at once.
 *
 * @returns {Promise<Array|null>} Merged per-company results, or null if any chunk failed
 *   — the caller decides what to do when the checks can't be run at all.
 */
export const runPreflightChecksForBatch = async (companies, platforms, startDate, endDate) => {
  const merged = [];
  for (let index = 0; index < companies.length; index += PREFLIGHT_COMPANY_LIMIT) {
    const chunk = companies.slice(index, index + PREFLIGHT_COMPANY_LIMIT);
    const response = await runPreflightChecks(chunk, platforms, startDate, endDate);
    if (!(response instanceof Response) || !response.ok) {
      return null;
    }
    const body = await response.json();
    merged.push(...(body.results ?? []));
  }
  return merged;
};
